import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Star, Building, CheckCircle, Video, MessageSquare, Wand2, Edit3, Send, Sparkles, AlertTriangle } from 'lucide-react';
import { useGetReviewByIdQuery, useGetVendorMeQuery, useUpdateReviewResponseMutation, useGenerateReviewResponseSuggestionMutation, useToggleFeatureTestimonialMutation, usePublishTestimonialMutation, downloadReviewPdf } from '../../store/vendorApi';
import { TestimonialResponse } from '@clareefai/types';
import ShareButtons from '../../components/public/ShareButtons';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';

const ReviewDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const formatStatus = (status: string | undefined): string => {
    if (!status) return '';
    const statusUpper = typeof status === 'string' ? status.toUpperCase() : String(status).toUpperCase();
    switch (statusUpper) {
      case 'COMPLETED':
        return t('reviews.status.completed');
      case 'PUBLISHED':
        return t('reviews.status.published');
      case 'FEATURED':
        return t('reviews.status.featured');
      default:
        return status;
    }
  };
  const reviewFromState = location.state?.review as TestimonialResponse | undefined;

  // Fetch the review using the new endpoint if we have promoterId, otherwise skip
  const { data: review, isLoading, error: reviewError } = useGetReviewByIdQuery(
    { promoterId: reviewFromState?.promoterId || '', testimonialId: id || '' },
    { skip: !reviewFromState?.promoterId || !id }
  );

  const { data: vendor } = useGetVendorMeQuery();
  const [updateReviewResponse, { isLoading: isSavingResponse }] = useUpdateReviewResponseMutation();
  const [generateResponseSuggestion, { isLoading: isGenerating }] = useGenerateReviewResponseSuggestionMutation();
  const [toggleFeature, { isLoading: isTogglingFeature }] = useToggleFeatureTestimonialMutation();
  const [publishTestimonial, { isLoading: isPublishing }] = usePublishTestimonialMutation();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Use fetched review if available, otherwise fallback to state
  const displayReview = review || reviewFromState;

  const [showResponseSection, setShowResponseSection] = useState(false);
  const [responseMode, setResponseMode] = useState<'ai' | 'manual'>('ai');
  const [responseText, setResponseText] = useState(displayReview?.vendorResponse || '');
  // Sync the editor with the saved response when a different review loads
  // (render-time state adjustment instead of an effect).
  const [syncedReviewId, setSyncedReviewId] = useState(displayReview?.id);
  if (displayReview && displayReview.id !== syncedReviewId) {
    setSyncedReviewId(displayReview.id);
    setResponseText(displayReview.vendorResponse || '');
  }

  if (isLoading && !displayReview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4339A0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('reviews.detail.loading')}</h2>
        </div>
      </div>
    );
  }

  if (!displayReview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('reviews.detail.notFound')}</h2>
          <p className="text-gray-600 mb-6">
            {t('reviews.detail.notFoundDescription')}
          </p>
          <button
            onClick={() => navigate('/reviews')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('reviews.detail.backToReviews')}
          </button>
          {reviewError && (
            <p className="text-xs text-gray-400 mt-4">
              {t('reviews.detail.error', { message: (reviewError as any)?.data?.message || t('reviews.detail.unknownError') })}
            </p>
          )}
        </div>
      </div>
    );
  }

  const generateAIResponse = async () => {
    setGenerateError(null);
    try {
      const { suggestion } = await generateResponseSuggestion({
        promoterId: displayReview.promoterId,
        testimonialId: displayReview.id,
      }).unwrap();
      setResponseText(suggestion);
    } catch (error) {
      setGenerateError(
        (error as any)?.data?.message || t('reviews.detail.generateError')
      );
    }
  };

  const handleSaveResponse = async () => {
    try {
      await updateReviewResponse({
        promoterId: displayReview.promoterId,
        testimonialId: displayReview.id,
        request: { response: responseText }
      }).unwrap();
      setShowResponseSection(false);
    } catch (error) {
      console.error('Error saving response:', error);
      alert(t('reviews.detail.saveResponseError'));
    }
  };

  const handleToggleFeatured = async () => {
    try {
      await toggleFeature({
        promoterId: displayReview.promoterId,
        testimonialId: displayReview.id
      }).unwrap();
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert(t('reviews.detail.toggleFeaturedError'));
    }
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    setExportError(null);
    try {
      await downloadReviewPdf(
        displayReview.promoterId,
        displayReview.id,
        `review-${displayReview.promoterName.replace(/\s+/g, '-')}.pdf`
      );
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('reviews.detail.exportFailed'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handlePublish = async (isPublished: boolean) => {
    try {
      await publishTestimonial({
        promoterId: displayReview.promoterId,
        testimonialId: displayReview.id,
        isPublished,
      }).unwrap();
    } catch (error) {
      console.error('Error updating publish status:', error);
      alert(t('reviews.detail.publishUpdateError'));
    }
  };

  // Return to the list with a reverse view transition: the detail header morphs
  // back into the matching card. We pass the id so the list can tag that card.
  const goBack = () => {
    const startViewTransition = (document as any).startViewTransition?.bind(document);
    if (!startViewTransition) {
      navigate('/reviews');
      return;
    }
    startViewTransition(() => {
      flushSync(() => navigate('/reviews', { state: { vtReturnId: displayReview.id } }));
    });
  };

  const statusPillClass =
    displayReview.status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100' :
      displayReview.status === 'FEATURED' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100' :
        displayReview.status === 'COMPLETED' ? 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' :
          'bg-rose-50 text-rose-700 ring-1 ring-rose-100';
  const canFeature = displayReview.status === 'PUBLISHED' || displayReview.status === 'FEATURED';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('reviews.detail.backToReviews')}</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#4339A0] to-[#6655d9] rounded-2xl p-6 text-white shadow-lg mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 flex-shrink-0 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-semibold text-xl">
              {displayReview.promoterName.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 style={{ viewTransitionName: 'review-title' }} className="text-2xl font-bold truncate">{displayReview.promoterName}</h1>
                {displayReview.videoUrl && (
                  <span className="inline-flex items-center gap-1 text-xs bg-white/15 border border-white/20 rounded-full px-2 py-0.5">
                    <Video className="w-3 h-3" /> {t('reviews.detail.video', 'Video')}
                  </span>
                )}
              </div>
              <p className="text-white/85 text-sm flex items-center gap-1.5 mt-1">
                <Building className="w-4 h-4" />
                <span style={{ viewTransitionName: 'review-company' }} className="truncate">{displayReview.promoterCompany || t('reviews.detail.noCompany')} · {displayReview.productId || t('reviews.detail.naShort')}</span>
              </p>
              <div className="flex items-center gap-4 flex-wrap mt-3">
                {displayReview.rating ? (
                  <div style={{ viewTransitionName: 'review-rating' }} className="flex items-center gap-0.5 w-fit">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < displayReview.rating! ? 'text-yellow-300 fill-current' : 'text-white/30'}`} />
                    ))}
                    <span className="text-sm text-white/80 ml-1.5">{displayReview.rating}/5</span>
                  </div>
                ) : null}
                <span className="text-sm text-white/70">
                  {new Date(displayReview.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                {displayReview.gdprConsent && (
                  <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-50 border border-emerald-300/30 rounded-full px-2.5 py-1">
                    <CheckCircle className="w-3 h-3" />
                    {t('reviews.detail.consentGiven', 'Consent given')}
                    {displayReview.consentedAt && (
                      <span className="text-emerald-100/80 ml-1">· {new Date(displayReview.consentedAt).toLocaleDateString('en-US')}</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Two-column layout: content + response on the left, actions on the right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Review content */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h2 className="text-base font-semibold text-gray-900 mb-3">{t('reviews.detail.reviewContent')}</h2>
              <div className="bg-gray-50 rounded-xl p-5 border-l-4 border-[#4339A0]">
                <p className="text-gray-800 leading-relaxed italic">"{displayReview.content || t('reviews.detail.noContentProvided')}"</p>
              </div>
            </div>

            {/* Company response */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">{t('reviews.detail.companyResponse')}</h2>
                {!showResponseSection && (
                  <button
                    onClick={() => setShowResponseSection(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{displayReview.vendorResponse ? t('reviews.detail.editResponse') : t('reviews.detail.addResponse')}</span>
                  </button>
                )}
              </div>

              {showResponseSection ? (
                <div className="space-y-4">
                  {/* AI / Manual segmented toggle */}
                  <div className="inline-flex rounded-lg bg-gray-100 p-1">
                    <button
                      onClick={() => setResponseMode('ai')}
                      className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${responseMode === 'ai' ? 'bg-white text-[#4339A0] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <Wand2 className="w-4 h-4" /> {t('reviews.detail.aiGenerated')}
                    </button>
                    <button
                      onClick={() => setResponseMode('manual')}
                      className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${responseMode === 'manual' ? 'bg-white text-[#4339A0] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <Edit3 className="w-4 h-4" /> {t('reviews.detail.manual')}
                    </button>
                  </div>

                  {responseMode === 'ai' && (
                    <div className="flex items-center justify-between gap-4 bg-gradient-to-r from-[#f5f3ff] to-[#ede9fe] rounded-xl p-4 border border-[#4339A0]/15">
                      <div className="flex items-start gap-3 min-w-0">
                        <Sparkles className="w-5 h-5 text-[#4339A0] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-700">{t('reviews.detail.aiResponseDescription')}</p>
                      </div>
                      <button
                        onClick={generateAIResponse}
                        disabled={isGenerating}
                        className="flex-shrink-0 flex items-center gap-2 bg-[#4339A0] text-white px-4 py-2 text-sm rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isGenerating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>{t('reviews.detail.generating')}</span>
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4" />
                            <span>{t('reviews.detail.generateResponse')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {generateError && (
                    <p className="text-sm text-red-600">{generateError}</p>
                  )}

                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={6}
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none text-gray-800"
                    placeholder={responseMode === 'ai'
                      ? t('reviews.detail.responseAiPlaceholder')
                      : t('reviews.detail.responseManualPlaceholder')
                    }
                  />

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setResponseText(displayReview.vendorResponse || '');
                        setShowResponseSection(false);
                      }}
                      className="px-5 py-2.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleSaveResponse}
                      disabled={!responseText.trim() || isSavingResponse}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {isSavingResponse ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('reviews.detail.saving')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>{t('reviews.detail.saveResponse')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : displayReview.vendorResponse ? (
                <div className="bg-[#f5f3ff] border-l-4 border-[#4339A0] p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 bg-[#4339A0] rounded-full flex items-center justify-center">
                      <Building className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{t('reviews.detail.companyResponse')}</span>
                  </div>
                  <p className="text-gray-800 leading-relaxed">{displayReview.vendorResponse}</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-200 rounded-xl">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-700">{t('reviews.detail.noResponseYet')}</p>
                  <p className="text-sm">{t('reviews.detail.clickToAddResponse')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action panel */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">{t('reviews.detail.actions')}</h2>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusPillClass}`}>
                  {formatStatus(displayReview.status)}
                </span>
              </div>

              <div className="space-y-2.5">
                {/* 1. Publish / Unpublish */}
                {displayReview.status === 'COMPLETED' ? (
                  <button
                    onClick={() => handlePublish(true)}
                    disabled={isPublishing}
                    className="w-full flex items-center justify-center gap-2 h-11 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {isPublishing ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>{t('reviews.detail.publishing')}</span></>
                    ) : (
                      <><CheckCircle className="w-4 h-4" /><span>{t('reviews.detail.publishReview')}</span></>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePublish(false)}
                    disabled={isPublishing}
                    className="w-full flex items-center justify-center gap-2 h-11 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                  >
                    {isPublishing ? (
                      <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /><span>{t('reviews.detail.updating')}</span></>
                    ) : (
                      <><AlertTriangle className="w-4 h-4" /><span>{t('reviews.detail.unpublishReview')}</span></>
                    )}
                  </button>
                )}

                {/* 2. Respond */}
                <button
                  onClick={() => setShowResponseSection(true)}
                  className="w-full flex items-center justify-center gap-2 h-11 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>{displayReview.vendorResponse ? t('reviews.detail.editResponse') : t('reviews.detail.respondToReview')}</span>
                </button>

                {/* 3. Feature */}
                <button
                  onClick={handleToggleFeatured}
                  disabled={isTogglingFeature || !canFeature}
                  title={!canFeature ? t('reviews.detail.featureRequiresPublish', 'Publish the review before featuring it') : undefined}
                  className={`w-full flex items-center justify-center gap-2 h-11 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed border ${displayReview.isFeatured
                    ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {isTogglingFeature ? (
                    <><div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" /><span>{t('reviews.detail.updating')}</span></>
                  ) : (
                    <><Star className={`w-4 h-4 ${displayReview.isFeatured ? 'fill-current' : ''}`} /><span>{displayReview.isFeatured ? t('reviews.detail.removeFromFeatured') : t('reviews.detail.makeFeatured')}</span></>
                  )}
                </button>

                {/* 4. Export PDF */}
                <button
                  onClick={handleExportPdf}
                  disabled={isExportingPdf}
                  className="w-full flex items-center justify-center gap-2 h-11 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isExportingPdf ? (
                    <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /><span>{t('reviews.detail.exporting')}</span></>
                  ) : (
                    <><Download className="w-4 h-4" /><span>{t('reviews.detail.exportPdf')}</span></>
                  )}
                </button>
                {exportError && <p className="text-xs text-red-600">{exportError}</p>}
              </div>

              {/* Share — only a published/featured review is publicly visible */}
              <div className="border-t border-gray-100 mt-5 pt-5">
                <p className="text-sm font-medium text-gray-700 mb-3">{t('reviews.detail.shareTitle')}</p>
                {canFeature ? (
                  <ShareButtons
                    compact
                    url={`${PUBLIC_SITE_URL}/review/${displayReview.id}${vendor?.pageInfo?.pageSlug ? `?slug=${vendor.pageInfo.pageSlug}` : ''}`}
                    title={displayReview.rating
                      ? t('reviews.detail.shareLabel', { rating: displayReview.rating, name: displayReview.promoterName })
                      : t('reviews.detail.shareLabelNoRating', { name: displayReview.promoterName })
                    }
                  />
                ) : (
                  <p className="text-sm text-gray-400">{t('reviews.detail.shareRequiresPublish')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetail;
